import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AlertTone } from '@/shared/ui/Alert'
import { Alert } from '@/shared/ui/Alert'
import { ORDER_COMMAND_POLL_MS, useGetOrderCommandQuery } from '../api/orderLineApi'
import { isOrderCommandSettled, orderCommandTypeOf } from '../model/commands'
import type { OrderCommandStatus } from '../model/operations'

export type OrderCommandAlertProps = {
  /** Команда обмена с 1С. `null` — файла в 1С нет (заказной раздел/сборка): результат готов сразу. */
  commandId: string | null
  /** Что отправлено: номер заказа или счёта — готовый заголовок с места вызова. */
  heading: string
}

const TONE: Record<OrderCommandStatus, AlertTone> = {
  accepted: 'accent',
  sent: 'accent',
  done: 'success',
  failed: 'danger',
  expired: 'warning',
}

/**
 * Состояние команды обмена с 1С на месте результата операции (резерв списком, дозаказ в счёт).
 * Пока команда не закрыта — опрашиваем; WS §0 инвалидирует тег `Command` сам, опрос — запасной
 * путь, он гаснет на терминальном статусе.
 *
 * `orders.merge`/`orders.split` 1С не исполняет — такая команда ответа не дождётся и закроется по
 * сроку. Эти операции результат не ждут вовсе (своя плашка «ждите уведомления»), но если тип всё
 * же пришёл сюда, опрос не крутим впустую: сужаем `type` известным перечнем фронта.
 */
export function OrderCommandAlert({ commandId, heading }: OrderCommandAlertProps) {
  const { t } = useTranslation('order')
  const [poll, setPoll] = useState(true)
  const command = useGetOrderCommandQuery(commandId ?? '', {
    skip: commandId === null,
    pollingInterval: poll ? ORDER_COMMAND_POLL_MS : 0,
  })

  if (commandId === null) {
    return (
      <Alert tone="success" heading={heading} announce="polite">
        {t('command.status.ready')}
      </Alert>
    )
  }

  const status = command.data?.status
  const kind = command.data === undefined ? null : orderCommandTypeOf(command.data.type)
  const unanswered = kind === 'orders.merge' || kind === 'orders.split'
  if (poll && (unanswered || isOrderCommandSettled(status))) setPoll(false)

  const tone: AlertTone = status === undefined ? 'accent' : TONE[status]
  return (
    <Alert tone={tone} heading={heading} announce="polite">
      {unanswered
        ? t('command.status.unanswered')
        : status === undefined
          ? t('command.status.pending')
          : t(`command.status.${status}`)}
    </Alert>
  )
}
